import { CardRecord, VocabMeta, VocabContent } from '../../db/indexedDb';
import { getSynonymsFor } from '../../config/vocabSynonyms';

export type VocabPracticeMode = 'input' | 'output' | 'speaking';

export interface NormalizedVocabMeta {
  en: string;
  cn: string;
  examples: string[];
  scene?: string;
  acceptableAnswers: string[];
  important: boolean;
}

export type VocabPracticeCard = CardRecord & {
  practiceMode: VocabPracticeMode;
  practiceId: string;
  vocabMeta: NormalizedVocabMeta;
};

export interface BuildVocabQueueOptions {
  outputMultiplierImportant?: number;
  outputMultiplierDefault?: number;
}

const coerceExamples = (meta?: VocabMeta, content?: VocabContent) => {
  const examples = meta?.examples ?? [];
  const exampleSentence = meta?.exampleSentence;
  const contentExample = typeof content?.example === 'string' ? [content.example] : [];
  const source = Array.isArray(examples) && examples.length > 0 ? examples : exampleSentence ? [exampleSentence] : contentExample;
  return source.map((item) => (item ?? '').toString().trim()).filter(Boolean);
};

export const normalizeAnswerText = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

export const normalizeVocabMeta = (card: CardRecord): NormalizedVocabMeta => {
  const meta = card.vocabMeta ?? {};
  const content = (card.content ?? {}) as VocabContent;

  const en = (meta.en ?? meta.front ?? content.term ?? '').toString().trim();
  const cn = (meta.cn ?? meta.back ?? content.definition ?? '').toString().trim();
  const scene = (meta.scene ?? meta.extraMeta?.scene ?? content.scene ?? '').toString().trim() || undefined;
  const examples = coerceExamples(meta, content);
  const important = Boolean(meta.important ?? meta.extraMeta?.important ?? false);

  const baseAnswers = [
    ...(Array.isArray(meta.acceptableAnswers) ? meta.acceptableAnswers : []),
    en,
    ...getSynonymsFor(en),
  ];

  const seen = new Set<string>();
  const acceptableAnswers = baseAnswers
    .map((item) => (item ?? '').toString().trim())
    .filter(Boolean)
    .filter((candidate) => {
      const norm = normalizeAnswerText(candidate);
      if (!norm || seen.has(norm)) return false;
      seen.add(norm);
      return true;
    });

  return {
    en,
    cn,
    scene,
    examples,
    acceptableAnswers,
    important,
  };
};

export const buildVocabQueue = (cards: CardRecord[], opts: BuildVocabQueueOptions = {}): VocabPracticeCard[] => {
  const importantMultiplier = Math.max(1, opts.outputMultiplierImportant ?? 2);
  const defaultMultiplier = Math.max(1, opts.outputMultiplierDefault ?? 1);
  const queue: VocabPracticeCard[] = [];

  cards
    .filter((card) => card.type === 'vocab')
    .forEach((card) => {
      const normalized = normalizeVocabMeta(card);
      const practiceBase = { ...card, vocabMeta: normalized };

      queue.push({ ...practiceBase, practiceMode: 'input', practiceId: `${card.id}#input` });

      const outputTimes = normalized.important ? importantMultiplier : defaultMultiplier;
      for (let i = 0; i < outputTimes; i += 1) {
        queue.push({
          ...practiceBase,
          practiceMode: 'output',
          practiceId: `${card.id}#output-${i + 1}`,
          priority: (card.priority ?? 0) + (normalized.important ? 5 : 2),
        });
      }

      // 为 speaking-phrase 标签的卡片添加口语练习任务
      const isSpeakingPhrase = (card.tags ?? []).includes('speaking-phrase');
      if (isSpeakingPhrase) {
        queue.push({
          ...practiceBase,
          practiceMode: 'speaking',
          practiceId: `${card.id}#speaking`,
          priority: (card.priority ?? 0) + 8, // 口语练习优先级更高
        });
      }
    });

  return queue;
};
